import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  ChecklistLinkedEntityType,
  ChecklistQuery,
  CreateChecklistRequest,
  ReviewChecklistRequest,
  SubmitChecklistRequest,
  UpdateChecklistRequest,
} from '@troia-v3/types';

import { checklistsApi } from '../services/checklistsApi';

/** Molde: `automation/forms/hooks/useForms.ts`. */

export const useChecklists = (query?: ChecklistQuery) => {
  return useQuery({
    queryKey: ['checklists', query],
    queryFn: () => checklistsApi.list(query),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useChecklist = (id?: string) => {
  return useQuery({
    queryKey: ['checklist', id],
    queryFn: () => checklistsApi.getById(id!),
    enabled: !!id,
    staleTime: 15 * 1000,
  });
};

/** "Meus checklists" — assignee OU follower (Task 16). */
export const useMyChecklists = (query?: ChecklistQuery) => {
  return useQuery({
    queryKey: ['checklists', 'mine', query],
    queryFn: () => checklistsApi.myChecklists(query),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Checklists vinculados a um lead/ticket — aba dos drawers (Task 17).
 * `enabled` (default `true`) condiciona ao `isOpen` do drawer (CLAUDE.md SEMPRE #10).
 */
export const useChecklistsByEntity = (
  type: ChecklistLinkedEntityType | undefined,
  id: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['checklists', 'by-entity', type, id],
    queryFn: () => checklistsApi.byEntity(type!, id!),
    enabled: enabled && !!type && !!id,
    staleTime: 30 * 1000,
  });
};

/** Cria uma instância a partir de um modelo (Task 16/17 — sem UI nesta task). */
export const useCreateChecklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChecklistRequest) => checklistsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      toast.success('Checklist criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao criar checklist');
    },
  });
};

export const useUpdateChecklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateChecklistRequest }) => checklistsApi.update(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklist', updated.id] });
      toast.success('Checklist atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao atualizar checklist');
    },
  });
};

export const useReviewChecklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReviewChecklistRequest }) => checklistsApi.review(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklist', updated.id] });
      toast.success(updated.status === 'approved' ? 'Checklist aprovado!' : 'Checklist reprovado.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao revisar checklist');
    },
  });
};

export const useReopenChecklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checklistsApi.reopen(id),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklist', updated.id] });
      toast.success('Checklist reaberto para correção.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao reabrir checklist');
    },
  });
};

export const useStartChecklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checklistsApi.start(id),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklist', updated.id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao iniciar checklist');
    },
  });
};

export const useSubmitChecklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SubmitChecklistRequest }) => checklistsApi.submit(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      queryClient.invalidateQueries({ queryKey: ['checklist', updated.id] });
      toast.success('Checklist enviado!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao enviar checklist');
    },
  });
};
